import * as CL from '@design-system/component-library';
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDebounce } from '../../../../common/hooks/useDebounce';
import {
  addressNumberOfBusinessPremises,
  addressSearchByExample,
  addressSearchById,
  loadingProductAvailabilityMsg,
  noSubscriptionsErrorMsg,
  searchMsg,
  somethingWentWrongMsg,
  t,
} from '../../../../common/i18n/index.js';
import { POSTAL_CODE_REGEX } from '../../../../common/utils/validationUtils.js';
import { PostalCodeInput } from '../../../PostalCodeInput/PostalCodeInput';
import { type AvailabilityAddress, type OpenFormAnswers } from '../../OpenFormAnswers.js';
import { OpenFormButton } from '../../OpenFormComponents/OpenFormButton';
import { OpenFormGridCol, OpenFormGridRow } from '../../OpenFormComponents/OpenFormGrid.js';
import { useAbortController } from '../../OpenFormHooks/useAbortController.js';
import { useOpenFormGoto } from '../../OpenFormHooks/useOpenFormGoto.js';
import { useOpenFormAsync } from '../../OpenFormProvider.js';
import { capitalize, concat } from '../../OpenFormUtils.js';
import type { AdminAddressOption } from '../../../../generated/api/adminAddressOption';
import type { AdminAddressSearchResponse } from '../../../../generated/api/adminAddressSearchResponse.js';
import type { OpenFormSection } from '../../../../generated/api/openFormSection.js';

const getSelectedValue = ({ addressText = '', addressId = '' } = {}) => addressText + addressId;

const AddressCombobox = ({
  addressId,
  addressText,
  apartmentType,
  buildingDescription,
  numberOfBusinessPremises,
  statusOfBuilding,
}: AdminAddressOption) => ({
  html: (
    <div className="of-openform__address-check__combobox">
      <label>{addressText}</label>
      <div>
        <span>
          {concat(addressId, buildingDescription, capitalize(statusOfBuilding), capitalize(apartmentType)).join(' | ')}
        </span>
        <span>{t.CPDQ(addressNumberOfBusinessPremises, numberOfBusinessPremises || '0')}</span>
      </div>
    </div>
  ),
  id: addressId,
  label: addressText,
  value: getSelectedValue({ addressText, addressId }),
});

const SuggestionCombobox = ({ addressText }: AdminAddressOption, index: number) => ({
  html: (
    <div className="of-openform__address-check__combobox">
      <label>{addressText}</label>
    </div>
  ),
  id: String(addressText + index),
  label: addressText,
  value: addressText,
});

export const OpenFormPageAvailability = ({
  section,
  answers,
  disabled,
  address = answers.get(section)?.address,
}: {
  section: OpenFormSection;
  answers: OpenFormAnswers;
  disabled: boolean;
  address?: AvailabilityAddress;
}) => {
  const { setValue } = useFormContext();
  const async = useOpenFormAsync();
  const goto = useOpenFormGoto();
  const [abortController, setAbortController] = useAbortController();
  const combo = useRef<HTMLButtonElement | null>(null);
  const input = useRef(address?.match?.addressText ?? '');
  const [postalCode, setPostalCode] = useState(address?.match?.postalCode ?? '');
  const [selectedValue, setSelectedValue] = useState(getSelectedValue(address?.match));
  const [matched, setMatched] = useState<AdminAddressSearchResponse>({
    matchedAddresses: [],
    matchedSuggestions: [],
  });

  const items = useMemo(() => {
    const { matchedAddresses = [], matchedSuggestions = [] } = matched ?? {};
    return matchedAddresses.map(AddressCombobox).concat(matchedSuggestions.map(SuggestionCombobox));
  }, [matched]);

  const onInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.value) {
        abortController?.abort();
        setMatched({ matchedAddresses: [], matchedSuggestions: [] });
        setValue(section.guid, undefined);
      }
      setSelectedValue((input.current = e.target.value));
    },
    [section, setValue, abortController]
  );

  const onPostalCode = useCallback(
    (code = '') => {
      abortController?.abort();
      setMatched({ matchedAddresses: [], matchedSuggestions: [] });
      setValue(section.guid, undefined);
      setPostalCode(code);
      setSelectedValue((input.current = ''));
    },
    [section, setValue, abortController]
  );

  const onSearch = useCallback(() => {
    setMatched({ matchedAddresses: [], matchedSuggestions: [] });
    const query = input.current.trim();
    if (postalCode && query.length > 1) {
      const { controller } = async.fetch
        .getAddressSearch({ postalCode, query })
        .resolved(result => {
          setMatched({
            matchedAddresses: result?.matchedAddresses ?? [],
            matchedSuggestions: result?.matchedSuggestions ?? [],
          });
        })
        .rejected(() => async.actions.notification({ text: t.JDTO(somethingWentWrongMsg), type: 'error' }))
        .cache(postalCode, query)
        .execute({ disable: false, message: true });
      setAbortController(controller);
    }
  }, [async, setAbortController, postalCode]);

  const onSubmit = useCallback(() => {
    const formId = async.state.form?.formId;
    const match = address?.match;
    if (formId && match) {
      const subscriptionTypes = answers.valuesOf('subscriptionTypes').flat();
      const { controller } = async.fetch
        .getOpenFormProductAvailability({ addressId: match.addressId, formId, subscriptionTypes })
        .resolved(products => setValue(section.guid + '.address', { match, products }))
        .rejected(() => async.actions.notification({ text: t.SBUB(noSubscriptionsErrorMsg), type: 'error' }))
        .settled(() => goto({ page: 'next' }))
        .execute({ disable: true, message: t.UR0I(loadingProductAvailabilityMsg) });
      setAbortController(controller);
    }
  }, [answers, section, address, setValue, async, goto, setAbortController]);

  const onValueSelect = useCallback(
    (value = '', item?: HTMLLIElement) => {
      const match = matched.matchedAddresses?.find(({ addressId }) => addressId === item?.id);
      if (match?.addressText) {
        setValue(section.guid + '.address', { match });
        setPostalCode(match.postalCode);
        setSelectedValue((input.current = match.addressText) && getSelectedValue(match));
      } else {
        setSelectedValue((input.current = value));
        requestAnimationFrame(() => combo.current?.click());
        onSearch();
      }
    },
    [section, setValue, matched, onSearch]
  );

  useEffect(onSearch, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <OpenFormGridRow className="of-openform__address-check">
      <OpenFormGridCol colWidth={6} className="of-openform__address-check__field-container label--mandatory">
        <PostalCodeInput disabled={disabled} postalCode={postalCode} setPostalCode={onPostalCode} />
        <CL.Combobox
          disabled={disabled || !POSTAL_CODE_REGEX.test(postalCode)}
          i18n_combobox_helpText={t.IKH4(addressSearchById)}
          inputProps={{ autoCapitalize: 'off', autoComplete: 'off', autoCorrect: 'off', onInput, value: input.current }}
          items={items}
          label={t.GL4R(addressSearchByExample)}
          selectedItemFormat="html"
          selectedValue={selectedValue}
          selectRef={btn => {
            combo.current = btn;
          }}
          onValueChange={useDebounce(onSearch, 500)}
          onValueSelect={onValueSelect}
        />
        <OpenFormButton
          className="of-openform__address-check__submit-button"
          disabled={disabled || !address?.match}
          size="l"
          text={t.VQE6(searchMsg)}
          onClick={onSubmit}
        />
      </OpenFormGridCol>
    </OpenFormGridRow>
  );
};
